import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	AlertCircle,
	ArrowLeft,
	CheckCircle2,
	Lock,
	Phone,
	ShieldCheck,
} from "lucide-react";
import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type KeyboardEvent,
	type ClipboardEvent,
} from "react";
import z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { authService } from "@/lib/auth-service";
import { API_BASE_URL } from "@/lib/consts";
import {
	trackPhoneVerificationStarted,
	trackPhoneVerificationCompleted,
} from "@/lib/analytics-events";

// --- Route definition ---

export const Route = createFileRoute("/auth/phone-verification")({
	component: PhoneVerificationPage,
	validateSearch: (search) =>
		z.object({ token: z.string().min(1).optional() }).parse(search),
});

// --- Main component ---

function PhoneVerificationPage() {
	const { token } = Route.useSearch();

	const [phase, setPhase] = useState<
		"phone_entry" | "code_entry" | "success"
	>("phone_entry");
	const [phoneMasked, setPhoneMasked] = useState("");

	if (!token) {
		return (
			<div className="min-h-screen flex items-center justify-center px-6">
				<div className="w-full max-w-sm text-center space-y-4">
					<AlertCircle className="size-8 text-destructive mx-auto" />
					<h1 className="text-xl font-semibold">Missing invite token</h1>
					<p className="text-muted-foreground text-sm">
						Please use the link from your invite email.
					</p>
				</div>
			</div>
		)
	}

	if (phase === "phone_entry") {
		return (
			<PhoneEntryScreen
				token={token}
				onCodeSent={(masked) => {
					setPhoneMasked(masked);
					setPhase("code_entry");
				}}
			/>
		)
	}

	if (phase === "code_entry") {
		return (
			<CodeEntryScreen
				token={token}
				phoneMasked={phoneMasked}
				onVerified={() => setPhase("success")}
				onBack={() => setPhase("phone_entry")}
			/>
		)
	}

	return <SuccessScreen />;
}

// --- Phone Entry Screen ---

function PhoneEntryScreen({
	token,
	onCodeSent,
}: {
	token: string;
	onCodeSent: (phoneMasked: string) => void;
}) {
	const [phone, setPhone] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const formatPhoneDisplay = (value: string) => {
		// Strip non-digits
		const digits = value.replace(/\D/g, "").slice(0, 10);
		if (digits.length <= 3) return digits;
		if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
		return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
	}

	const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value.replace(/\D/g, "").slice(0, 10);
		setPhone(raw);
		setError("");
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (phone.length !== 10) {
			setError("Please enter a valid 10-digit US phone number");
			return
		}

		setLoading(true);
		setError("");

		try {
			const res = await fetch(`${API_BASE_URL}/auth/phone/send-code`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					invite_token: token,
					phone_number: `+1${phone}`,
				}),
			})

			if (res.ok) {
				const data = await res.json();
				trackPhoneVerificationStarted();
			onCodeSent(data.phone_masked);
				return
			}

			const body = await res.json().catch(() => null);
			const detail = body?.detail;

			if (detail?.status === "locked_out") {
				setError(
					`Too many attempts. Please wait ${Math.ceil((detail.lockout_seconds || 900) / 60)} minutes.`,
				)
			} else {
				setError(detail?.message || "Failed to send verification code");
			}
		} catch {
			setError("Network error. Please try again.");
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="min-h-screen flex items-center justify-center px-6 py-12">
			<div className="w-full max-w-md space-y-8">
				{/* Header */}
				<div className="space-y-3">
					<div className="inline-flex items-center justify-center size-12 rounded-full bg-primary/10 text-primary">
						<ShieldCheck className="size-6" />
					</div>
					<h1 className="text-[28px] font-semibold tracking-tight text-foreground">
						Secure Your Account
					</h1>
					<p className="text-[15px] text-muted-foreground leading-relaxed">
						Your phone secures your account with 2-factor authentication,
						protecting your campaign from unauthorized access.
					</p>
				</div>

				{/* Phone input form */}
				<form onSubmit={handleSubmit} className="space-y-4">
					<div className="space-y-2">
						<label
							htmlFor="phone"
							className="text-sm font-medium text-foreground"
						>
							Phone Number
						</label>
						<div className="flex items-center gap-2">
							<div className="flex items-center h-9 px-3 rounded-md border border-input bg-muted text-sm text-muted-foreground shrink-0">
								+1
							</div>
							<Input
								id="phone"
								type="tel"
								placeholder="(555) 123-4567"
								value={formatPhoneDisplay(phone)}
								onChange={handlePhoneChange}
								autoFocus
								disabled={loading}
							/>
						</div>
						<p className="text-xs text-muted-foreground">
							US numbers only. Standard SMS rates may apply.
						</p>
					</div>

					{error && (
						<div className="flex items-start gap-2 text-sm text-destructive">
							<AlertCircle className="size-4 shrink-0 mt-0.5" />
							<span>{error}</span>
						</div>
					)}

					<Button
						type="submit"
						size="lg"
						className="w-full"
						disabled={loading || phone.length !== 10}
					>
						{loading ? (
							<>
								<Spinner className="size-4" />
								Sending...
							</>
						) : (
							<>
								<Phone className="size-4" />
								Send Verification Code
							</>
						)}
					</Button>
				</form>

				{/* Security note */}
				<div className="flex items-start gap-2 text-xs text-muted-foreground">
					<Lock className="size-4 shrink-0 mt-0.5" />
					<span>
						We will only use your phone number for account security and will
						never share it with third parties.
					</span>
				</div>
			</div>
		</div>
	)
}

// --- Code Entry Screen ---

function CodeEntryScreen({
	token,
	phoneMasked,
	onVerified,
	onBack,
}: {
	token: string;
	phoneMasked: string;
	onVerified: () => void;
	onBack: () => void;
}) {
	const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [, setRemainingAttempts] = useState<number | null>(
		null,
	)
	const [lockoutSeconds, setLockoutSeconds] = useState(0);
	const [resendCooldown, setResendCooldown] = useState(60);
	const [resendLoading, setResendLoading] = useState(false);

	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	// Resend cooldown timer
	useEffect(() => {
		if (resendCooldown <= 0) return;
		const timer = setInterval(() => {
			setResendCooldown((prev) => Math.max(0, prev - 1));
		}, 1000);
		return () => clearInterval(timer);
	}, [resendCooldown]);

	// Lockout countdown timer
	useEffect(() => {
		if (lockoutSeconds <= 0) return;
		const timer = setInterval(() => {
			setLockoutSeconds((prev) => Math.max(0, prev - 1));
		}, 1000);
		return () => clearInterval(timer);
	}, [lockoutSeconds]);

	// Auto-focus first input on mount
	useEffect(() => {
		inputRefs.current[0]?.focus();
	}, []);

	const submitCode = useCallback(
		async (code: string) => {
			setLoading(true);
			setError("");

			try {
				const res = await fetch(`${API_BASE_URL}/auth/phone/verify-code`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ invite_token: token, code }),
				})

				if (res.ok) {
					const data = await res.json();

					// Store tokens
					authService.setTokens({
						authToken: data.access_token,
						refreshToken: data.refresh_token,
						expiry: data.expires_in,
					})

					trackPhoneVerificationCompleted();
					onVerified()
					return
				}

				const body = await res.json().catch(() => null);
				const detail = body?.detail;

				if (detail?.status === "locked_out") {
					setLockoutSeconds(detail.lockout_seconds || 900);
					setError("Too many incorrect attempts. Please wait.");
				} else if (detail?.status === "wrong_code") {
					setRemainingAttempts(detail.remaining_attempts);
					setError(
						`Incorrect code. ${detail.remaining_attempts} attempt${detail.remaining_attempts === 1 ? "" : "s"} remaining.`,
					)
					// Clear digits and refocus
					setDigits(["", "", "", "", "", ""]);
					setTimeout(() => inputRefs.current[0]?.focus(), 50);
				} else if (detail?.status === "code_expired") {
					setError("Code has expired. Please request a new one.");
				} else {
					setError(detail?.message || "Verification failed. Please try again.");
				}
			} catch {
				setError("Network error. Please try again.");
			} finally {
				setLoading(false);
			}
		},
		[token, onVerified],
	)

	const handleDigitChange = (index: number, value: string) => {
		// Only accept single digits
		const digit = value.replace(/\D/g, "").slice(-1);
		const newDigits = [...digits];
		newDigits[index] = digit;
		setDigits(newDigits);
		setError("");

		if (digit && index < 5) {
			// Auto-advance to next input
			inputRefs.current[index + 1]?.focus();
		}

		// Auto-submit when all 6 digits are entered
		if (digit && index === 5) {
			const code = newDigits.join("");
			if (code.length === 6) {
				submitCode(code);
			}
		}
	}

	const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Backspace" && !digits[index] && index > 0) {
			// Move focus to previous input on backspace when current is empty
			inputRefs.current[index - 1]?.focus();
		}
	}

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
		if (pasted.length === 0) return;

		const newDigits = [...digits];
		for (let i = 0; i < pasted.length && i < 6; i++) {
			newDigits[i] = pasted[i];
		}
		setDigits(newDigits);

		// Focus last filled input or next empty one
		const focusIndex = Math.min(pasted.length, 5);
		inputRefs.current[focusIndex]?.focus();

		// Auto-submit if 6 digits pasted
		if (pasted.length === 6) {
			submitCode(pasted);
		}
	}

	const handleResend = async () => {
		setResendLoading(true);
		setError("");

		try {
			const res = await fetch(`${API_BASE_URL}/auth/phone/resend-code`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ invite_token: token }),
			})

			if (res.ok) {
				const data = await res.json();
				setResendCooldown(data.cooldown_seconds || 60);
				setDigits(["", "", "", "", "", ""]);
				setRemainingAttempts(null);
				setTimeout(() => inputRefs.current[0]?.focus(), 50);
				return
			}

			const body = await res.json().catch(() => null);
			const detail = body?.detail;

			if (detail?.status === "cooldown") {
				setResendCooldown(detail.cooldown_seconds || 60);
			} else {
				setError(detail?.message || "Could not resend code");
			}
		} catch {
			setError("Network error. Please try again.");
		} finally {
			setResendLoading(false);
		}
	}

	const isLockedOut = lockoutSeconds > 0;

	return (
		<div className="min-h-screen flex items-center justify-center px-6 py-12">
			<div className="w-full max-w-md space-y-8">
				{/* Header */}
				<div className="space-y-3">
					<h1 className="text-[28px] font-semibold tracking-tight text-foreground">
						Enter Verification Code
					</h1>
					<p className="text-[15px] text-muted-foreground">
						We sent a 6-digit code to{" "}
						<span className="font-medium text-foreground">{phoneMasked}</span>
					</p>
				</div>

				{/* Lockout state */}
				{isLockedOut && (
					<div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 text-destructive">
						<Lock className="size-5 shrink-0" />
						<div className="text-sm">
							<p className="font-medium">Too many attempts</p>
							<p>
								Please wait{" "}
								{lockoutSeconds >= 60
									? `${Math.ceil(lockoutSeconds / 60)} minute${Math.ceil(lockoutSeconds / 60) === 1 ? "" : "s"}`
									: `${lockoutSeconds} second${lockoutSeconds === 1 ? "" : "s"}`}
							</p>
						</div>
					</div>
				)}

				{/* Code input */}
				<div className="space-y-4">
					<div className="flex justify-center gap-3">
						{digits.map((digit, i) => (
							<input
								key={i}
								ref={(el) => {
									inputRefs.current[i] = el
								}}
								type="text"
								inputMode="numeric"
								maxLength={1}
								value={digit}
								onChange={(e) => handleDigitChange(i, e.target.value)}
								onKeyDown={(e) => handleKeyDown(i, e)}
								onPaste={i === 0 ? handlePaste : undefined}
								disabled={loading || isLockedOut}
								className="w-12 h-14 text-center text-xl font-semibold rounded-lg border border-input bg-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
								autoComplete="one-time-code"
							/>
						))}
					</div>

					{loading && (
						<div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
							<Spinner className="size-4" />
							<span>Verifying...</span>
						</div>
					)}

					{error && !isLockedOut && (
						<div className="flex items-start gap-2 text-sm text-destructive justify-center">
							<AlertCircle className="size-4 shrink-0 mt-0.5" />
							<span>{error}</span>
						</div>
					)}
				</div>

				{/* Resend and back links */}
				<div className="space-y-3 text-center">
					<div className="text-sm text-muted-foreground">
						{"Didn't receive it? "}
						{resendCooldown > 0 ? (
							<span>Resend code in {resendCooldown}s</span>
						) : (
							<button
								type="button"
								onClick={handleResend}
								disabled={resendLoading || isLockedOut}
								className="text-primary hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{resendLoading ? "Sending..." : "Resend code"}
							</button>
						)}
					</div>

					<button
						type="button"
						onClick={onBack}
						disabled={loading}
						className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
					>
						<ArrowLeft className="size-3.5" />
						Use a different phone number
					</button>
				</div>
			</div>
		</div>
	)
}

// --- Success Screen ---

function SuccessScreen() {
	const navigate = useNavigate();

	useEffect(() => {
		const timer = setTimeout(() => {
			navigate({ to: "/" });
		}, 2000);
		return () => clearTimeout(timer);
	}, [navigate]);

	return (
		<div className="min-h-screen flex items-center justify-center px-6">
			<div className="w-full max-w-sm text-center space-y-6">
				<div className="flex justify-center">
					<div className="inline-flex items-center justify-center size-16 rounded-full bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">
						<CheckCircle2 className="size-8" />
					</div>
				</div>
				<div className="space-y-2">
					<h1 className="text-2xl font-semibold text-foreground">
						Phone Verified!
					</h1>
					<p className="text-muted-foreground text-[15px]">
						Your account is secure. Redirecting...
					</p>
				</div>
				<Spinner className="size-5 text-muted-foreground mx-auto" />
			</div>
		</div>
	)
}
